/**
 * Org-scoped web-app URL builders and the task-URL parser shared by the web app and the scripts.
 *
 * Org pages live under `/orgs/{orgId}`: the trials home, the `traits|team|settings|scouting`
 * sections, and `/orgs/{orgId}/trials/{t}[/experiments/{e}[/tasks/{k}[/ai-traits]]]`. Legacy
 * `/trials/...` links without an org keep working in the web app through its resolver, so the
 * parser accepts both forms.
 *
 * Builders `encodeURIComponent` every segment; the parser decodes each captured segment exactly
 * once. Real ids are URL-safe (Firestore auto ids and `[A-Za-z0-9_-]` seed/script ids), so this is
 * for consistency rather than a live need.
 *
 * Compiled by the web app, the scripts and `functions/` (strict CommonJS), so it must not import
 * react-router or touch DOM globals.
 */

export type OrgSection = "traits" | "team" | "settings" | "scouting";

export interface ParsedAppTaskUrl {
  /** Present only when the URL is in the org form (`/orgs/{orgId}/trials/...`). */
  orgId?: string;
  trialId: string;
  experimentId: string;
  taskId: string;
}

const enc = encodeURIComponent;

export function orgHomePath(orgId: string): string {
  return `/orgs/${enc(orgId)}`;
}

export function orgSectionPath(orgId: string, section: OrgSection): string {
  return `${orgHomePath(orgId)}/${section}`;
}

export function trialPath(orgId: string, trialId: string): string {
  return `${orgHomePath(orgId)}/trials/${enc(trialId)}`;
}

export function experimentPath(orgId: string, trialId: string, experimentId: string): string {
  return `${trialPath(orgId, trialId)}/experiments/${enc(experimentId)}`;
}

export function taskPath(orgId: string, trialId: string, experimentId: string, taskId: string): string {
  return `${experimentPath(orgId, trialId, experimentId)}/tasks/${enc(taskId)}`;
}

export function taskAiTraitsPath(orgId: string, trialId: string, experimentId: string, taskId: string): string {
  return `${taskPath(orgId, trialId, experimentId, taskId)}/ai-traits`;
}

/** A task's Firestore document path (not an app URL); COCO's info.gleanag_task_path and export_info.json use it. */
export function firestoreTaskPath(orgId: string, trialId: string, experimentId: string, taskId: string): string {
  return `organizations/${orgId}/trials/${trialId}/experiments/${experimentId}/tasks/${taskId}`;
}

/** Join an origin (e.g. `getAppHost(env)`) and an app path without doubling the slash. */
export function appUrl(host: string, path: string): string {
  return `${host.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

// Unanchored on `/trials/` like the scripts' historical parsers, so a task path anywhere in the
// string matches; the org is only taken from an `/orgs/{id}` segment immediately before it.
const TASK_PATH_PATTERN = /(?:\/orgs\/([^/]+))?\/trials\/([^/]+)\/experiments\/([^/]+)\/tasks\/([^/]+)/;

/**
 * Extract the (optional) org and the trial/experiment/task ids from a web-app task URL: a full URL
 * on any host or a bare path, legacy or org form, with or without `/ai-traits`, query or hash.
 * Returns null when the input is not a task URL (or a segment is malformed percent-encoding).
 */
export function parseAppTaskUrl(urlOrPath: string): ParsedAppTaskUrl | null {
  const withoutQueryOrHash = urlOrPath.split(/[?#]/, 1)[0];
  const match = `/${withoutQueryOrHash}`.match(TASK_PATH_PATTERN);
  if (!match) return null;
  const [, orgId, trialId, experimentId, taskId] = match;
  try {
    return {
      ...(orgId !== undefined ? { orgId: decodeURIComponent(orgId) } : {}),
      trialId: decodeURIComponent(trialId),
      experimentId: decodeURIComponent(experimentId),
      taskId: decodeURIComponent(taskId),
    };
  } catch {
    return null;
  }
}

/** `parseAppTaskUrl` for callers that need a task URL: throws instead of returning null. */
export function requireAppTaskUrl(urlOrPath: string): ParsedAppTaskUrl {
  const parsed = parseAppTaskUrl(urlOrPath);
  if (!parsed) {
    throw new Error(
      `Not a GleanAg task URL: ${JSON.stringify(urlOrPath)}. Expected ` +
        ".../orgs/{orgId}/trials/{trialId}/experiments/{experimentId}/tasks/{taskId} " +
        "(or the legacy form without /orgs/{orgId})."
    );
  }
  return parsed;
}
